import React from 'react'
import PlacesAutocomplete from 'react-places-autocomplete'

import useGoogleMaps from 'hooks/useGoogleMaps'

/**
 * Drop-in replacement for `react-places-autocomplete` that loads the Google
 * Maps SDK on demand.
 *
 * `PlacesAutocomplete` throws "Google Maps JavaScript API library must be
 * loaded" if `window.google` is absent when it mounts, which is why the SDK
 * used to be a blocking `<script>` in `index.html` — fetched on every page of
 * the app, including the many with no address input. Google bills that per page
 * load (DT-012).
 *
 * This waits for the SDK, then renders the real component with the same props
 * and render-prop children, so call sites change only their import.
 *
 * While loading it renders a working text input bound to the same value, so the
 * form stays usable and nothing jumps in or out of the layout. If the SDK never
 * arrives — no API key, blocked network — the field simply stays plain text
 * rather than breaking the screen.
 */

interface PlacesAutocompleteLazyProps {
	value: string
	onChange: (value: string) => void
	onSelect?: (address: string, placeId?: string) => void
	children: (args: {
		getInputProps: (options?: Record<string, unknown>) => Record<string, unknown>
		suggestions: any[]
		getSuggestionItemProps: (
			suggestion: any,
			options?: Record<string, unknown>,
		) => Record<string, unknown>
		loading: boolean
	}) => React.ReactNode
	[key: string]: unknown
}

export const PlacesAutocompleteLazy = ({
	value,
	onChange,
	onSelect,
	children,
	...rest
}: PlacesAutocompleteLazyProps) => {
	const { ready } = useGoogleMaps()

	if (!ready) {
		// Hand the children the same shape the real component provides, with an
		// input bound to `value` and no suggestions. Every existing call site
		// spreads `getInputProps()` onto its own styled input, so the field keeps
		// its own appearance rather than being replaced by a generic one.
		return (
			<>
				{children({
					getInputProps: (options: Record<string, unknown> = {}) => ({
						...options,
						value,
						onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
					}),
					suggestions: [],
					getSuggestionItemProps: () => ({}),
					loading: false,
				})}
			</>
		)
	}

	return (
		<PlacesAutocomplete value={value} onChange={onChange} onSelect={onSelect} {...rest}>
			{children as never}
		</PlacesAutocomplete>
	)
}

export default PlacesAutocompleteLazy
